import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Bug, CheckCircle2, XCircle, Loader2, TestTube } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { usePushNotifications } from '@/hooks/usePushNotifications';

interface DebugEvent {
  timestamp: string;
  type: string;
  message: string;
  status: 'success' | 'error' | 'warning';
}

export function NotificationDebugPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { isSubscribed, subscribe } = usePushNotifications();
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  const [events, setEvents] = useState<DebugEvent[]>([]);
  const [testing, setTesting] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Check realtime connection
    const checkRealtime = async () => {
      const channels = supabase.getChannels();
      const hasActiveChannels = channels.some(ch => ch.state === 'joined');
      setRealtimeStatus(hasActiveChannels ? 'connected' : 'disconnected');
    };

    checkRealtime();
    const interval = setInterval(checkRealtime, 5000);
    return () => clearInterval(interval);
  }, []);

  const addEvent = (type: string, message: string, status: 'success' | 'error' | 'warning') => {
    setEvents(prev => [
      {
        timestamp: new Date().toLocaleTimeString('nl-NL'),
        type,
        message,
        status
      },
      ...prev.slice(0, 9) // Keep last 10 events
    ]);
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      await subscribe();
      addEvent('push', 'Push notificaties succesvol ingeschakeld', 'success');
      toast({
        title: 'Push notificaties ingeschakeld',
        description: 'Je ontvangt nu push notificaties',
      });
    } catch (error: any) {
      console.error('Subscribe error:', error);
      addEvent('push', `Fout bij inschakelen: ${error.message}`, 'error');
      toast({
        title: 'Push notificaties inschakelen mislukt',
        description: error.message === 'Notification permission denied' 
          ? 'Je hebt notificaties geblokkeerd. Ga naar je browser instellingen om dit te wijzigen.'
          : error.message,
        variant: 'destructive'
      });
    } finally {
      setSubscribing(false);
    }
  };

  const testTodoNotification = async () => {
    if (!user) return;
    setTesting('todo');

    try {
      // Insert notification directly to bypass realtime and self-assignment logic
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'todo',
          title: '✅ Test To-do Notificatie',
          description: 'Dit is een test notificatie direct vanuit het debug panel',
          is_read: false
        });

      if (error) throw error;

      addEvent('todo', 'Test notificatie direct in DB aangemaakt', 'success');
      toast({
        title: 'Test to-do notificatie verstuurd',
        description: 'Check je notificaties (klik op de bel rechtsbovenin)',
      });
    } catch (error: any) {
      console.error('Test todo failed:', error);
      addEvent('todo', `Fout: ${error.message}`, 'error');
      toast({
        title: 'Test mislukt',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(null);
    }
  };

  const testPushNotification = async () => {
    if (!user) return;
    setTesting('push');

    try {
      if (!isSubscribed) {
        addEvent('push', 'Geen push subscription - klik op "Push Notificaties Inschakelen"', 'warning');
        toast({
          title: 'Push niet ingeschakeld',
          description: 'Schakel eerst push notificaties in',
          variant: 'destructive'
        });
        setTesting(null);
        return;
      }

      // Trigger a test push via edge function
      const { error } = await supabase.functions.invoke('push-notifications', {
        body: {
          userId: user.id,
          title: '🔔 Test Push Notificatie',
          body: 'Als je dit ziet werken push notificaties!',
          url: '/crm/settings'
        }
      });

      if (error) throw error;

      addEvent('push', 'Test push verstuurd - check je apparaat', 'success');
      toast({
        title: 'Test push verstuurd',
        description: 'Check je browser/apparaat voor de notificatie',
      });
    } catch (error: any) {
      console.error('Test push failed:', error);
      addEvent('push', `Fout: ${error.message}`, 'error');
      toast({
        title: 'Test mislukt',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(null);
    }
  };

  const testLeadNotification = async () => {
    if (!user) return;
    setTesting('lead');

    try {
      // Create a notification directly (simulating a lead event)
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'lead',
          title: '🎯 Test Lead Notificatie',
          description: 'Nieuwe test lead ontvangen via quickscan',
          is_read: false
        });

      if (error) throw error;

      addEvent('lead', 'Test lead notificatie aangemaakt', 'success');
      toast({
        title: 'Test lead notificatie',
        description: 'Check je notificaties (klik op de bel rechtsbovenin)',
      });
    } catch (error: any) {
      console.error('Test lead failed:', error);
      addEvent('lead', `Fout: ${error.message}`, 'error');
      toast({
        title: 'Test mislukt',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="h-5 w-5" />
          Notificatie Debug Panel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Indicators */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm font-medium mb-2">Realtime Status</div>
            <div className="flex items-center gap-2">
              {realtimeStatus === 'checking' ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : realtimeStatus === 'connected' ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">Verbonden</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">Niet verbonden</span>
                </>
              )}
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm font-medium mb-2">Push Status</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {isSubscribed ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Ingeschakeld</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-600">Uitgeschakeld</span>
                  </>
                )}
              </div>
              {!isSubscribed && (
                <Button 
                  onClick={handleSubscribe} 
                  size="sm" 
                  variant="outline"
                  className="w-full text-xs"
                  disabled={subscribing}
                >
                  {subscribing ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Bell className="h-3 w-3 mr-1" />
                  )}
                  Push Inschakelen
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Test Buttons */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Test Notificaties</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={testTodoNotification}
              disabled={testing === 'todo'}
            >
              {testing === 'todo' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="mr-2 h-4 w-4" />
              )}
              Test To-do
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={testLeadNotification}
              disabled={testing === 'lead'}
            >
              {testing === 'lead' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="mr-2 h-4 w-4" />
              )}
              Test Lead
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={testPushNotification}
              disabled={testing === 'push' || !isSubscribed}
            >
              {testing === 'push' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Bell className="mr-2 h-4 w-4" />
              )}
              Test Push
            </Button>
          </div>

          {!isSubscribed && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>Push notificaties:</strong> Scroll naar beneden naar "Notificatie Voorkeuren" 
                en klik op "Push Notificaties Inschakelen" om push tests mogelijk te maken.
              </p>
            </div>
          )}
        </div>

        {/* Recent Events Log */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Recent Events</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {events.length === 0 ? (
              <div className="p-4 rounded-lg bg-muted text-center text-sm text-muted-foreground">
                Geen events nog. Klik op een test knop om te beginnen.
              </div>
            ) : (
              events.map((event, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg bg-muted text-sm space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">
                      {event.timestamp}
                    </span>
                    <Badge
                      variant={
                        event.status === 'success'
                          ? 'default'
                          : event.status === 'error'
                          ? 'destructive'
                          : 'secondary'
                      }
                      className="text-xs"
                    >
                      {event.type}
                    </Badge>
                  </div>
                  <p className="whitespace-pre-wrap">{event.message}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 rounded-lg bg-muted space-y-2">
          <h4 className="text-sm font-medium">💡 Gebruik deze tool om:</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Realtime channels te testen (unified_todos, tickets, project_notes)</li>
            <li>• Push notificatie delivery te verifiëren</li>
            <li>• Notificatie voorkeuren werking te controleren</li>
            <li>• Console logs te bekijken (open Browser DevTools → Console)</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
